import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { TranslationsFunc, useTranslations } from '@lang';
import { useRates, useRatePageSettings } from '@features/rates';
import { useRoomPageSettings } from '@features/rooms';
import { parseDateToString } from '@utils/helpers';
import { queryParamToString } from '@utils/queryParam';
import { AccommodationListItem } from 'voyager';
import { useRouter } from 'shared-routing';
import { Rate } from '@features/rates/types';

export const mapRateToListItem = (
  rate: Rate,
  t: TranslationsFunc,
  fallbackImageUrl?: string
): AccommodationListItem => ({
  id: rate.id,
  name: rate.title,
  imageUrl: rate.imageUrl || fallbackImageUrl || undefined,
  price:
    rate.price && !rate.unAvailable
      ? { amount: rate.price.amount, currencyCode: rate.price.currencyCode }
      : undefined,
  unavailable: rate.unAvailable,
  unavailableLabel: t('common.unavailableForSearch'),
});

export function useRatesList(journeyParams: ParsedJourneyParams, language: string) {
  const { t } = useTranslations();
  const router = useRouter();

  const { content: ratesContent, isFetching: isFetchingRates } = useRatePageSettings(
    language,
    t('common.rate')
  );
  const { content: roomsContent, isFetching: isFetchingRooms } = useRoomPageSettings(
    language,
    t('common.rate')
  );

  const promoRateCode =
    queryParamToString(router.query.promoRateCode) ?? journeyParams.promoRateCode;

  const adults = journeyParams.adults ?? 0;
  const children = journeyParams.children ?? 0;
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];

  const {
    rates,
    isFetching: isFetchingRateData,
    error,
  } = useRates({
    fromDate: parseDateToString(journeyParams.fromDate),
    toDate: parseDateToString(journeyParams.toDate),
    adults,
    children,
    promoRateCode,
    childAges: journeyParams.childAges,
    rooms,
    language,
  });

  const fallbackImageUrl =
    ratesContent?.heroBackgroundImage ?? roomsContent?.heroBackgroundImage ?? undefined;

  const listItems: AccommodationListItem[] = rates.map((rate) =>
    mapRateToListItem(rate, t, fallbackImageUrl ?? undefined)
  );

  return {
    listItems,
    content: ratesContent,
    fallbackTitle: roomsContent?.title,
    isFetching: isFetchingRateData || isFetchingRates || isFetchingRooms,
    error,
  };
}
